import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useWorkspaceScope } from "../../../shared/hooks/useWorkspaceScope";
import type { PurchaseOrder, PurchaseOrderCandidate, PurchaseOrderManagementOverview } from "../domain/models";
import { purchaseOrderGateway } from "../infrastructure/api/purchase-order-gateway";
import type { PurchaseOrdersProps, PurchaseOrderFilter } from "./purchase-order-workspace-types";
import { purchaseOrderScopeKey } from "./purchase-order-scope-key";
import { purchaseOrderFocusFilter } from "./purchase-order-presentation";
import { mergePurchaseOrderRead } from "./purchase-order-read-state";

type Props = Pick<PurchaseOrdersProps, "user" | "notify" | "initialRequestNo" | "dataRefreshVersion"> & {
  previewKey: string | null;
  onFocusOrder: (order: PurchaseOrder, filter: PurchaseOrderFilter) => void;
  onFocusCandidate: (candidate: PurchaseOrderCandidate) => void;
};
type Scope = ReturnType<typeof useWorkspaceScope<[string, ("success" | "error")?]>>;
type State = { scope: Scope; orders: PurchaseOrder[]; candidates: PurchaseOrderCandidate[]; management: PurchaseOrderManagementOverview | null; loading: boolean; loaded: boolean; loadError: string };
const empty = (scope: Scope): State => ({ scope, orders: [], candidates: [], management: null, loading: true, loaded: false, loadError: "" });

export function usePurchaseOrdersData({ user, notify, initialRequestNo, dataRefreshVersion, previewKey, onFocusOrder, onFocusCandidate }: Props) {
  const scope = useWorkspaceScope(purchaseOrderScopeKey(user, previewKey), notify);
  const [state, setState] = useState(() => empty(scope));
  const currentState = useRef(state);
  const clock = useRef({ scope, load: 0, writes: 0, candidates: 0, orders: new Map<string, { serial: number; order: PurchaseOrder }>() });
  if (clock.current.scope !== scope) clock.current = { scope, load: 0, writes: 0, candidates: 0, orders: new Map() };
  const focusRef = useRef({ scope, initialRequestNo });
  if (focusRef.current.scope !== scope || focusRef.current.initialRequestNo !== initialRequestNo) focusRef.current = { scope, initialRequestNo };
  const focus = focusRef.current;
  const handledFocus = useRef<typeof focus | null>(null);
  const callbacks = useRef({ onFocusOrder, onFocusCandidate });
  callbacks.current = { onFocusOrder, onFocusCandidate };
  const isSpecialist = user.role === "SATIN_ALMA";
  const isPlanning = user.role === "PLANLAMA";
  const isQuality = user.role === "KALITE_UZMANI" || user.role === "KALITE_MUDURU";
  const isRolePreview = Boolean(previewKey);
  const isManager = ["SATIN_ALMA_MUDURU", "GENEL_MUDUR"].includes(user.role);

  const commit = useCallback((change: (previous: State) => State) => {
    if (!scope.isCurrent()) return undefined;
    const previous = currentState.current.scope === scope ? currentState.current : empty(scope);
    const next = change(previous);
    currentState.current = next;
    setState(next);
    return next;
  }, [scope]);

  const setCandidates: Dispatch<SetStateAction<PurchaseOrderCandidate[]>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    clock.current.candidates++;
    commit(previous => {
      return { ...previous, candidates: typeof value === "function" ? value(previous.candidates) : value };
    });
  }, [scope, commit]);
  const replaceOrder = useCallback((updated: PurchaseOrder) => {
    if (!scope.isCurrent()) return;
    const previousWrite = clock.current.orders.get(updated.id);
    if (previousWrite && previousWrite.order.version > updated.version) return;
    clock.current.orders.set(updated.id, { serial: ++clock.current.writes, order: updated });
    commit(previous => {
      return { ...previous, orders: mergePurchaseOrderRead(previous.orders, previous.orders, [updated]) };
    });
  }, [scope, commit]);
  const load = useCallback(async () => {
    if (!scope.isCurrent()) return false;
    const snapshot = { load: ++clock.current.load, writes: clock.current.writes, candidates: clock.current.candidates };
    const isCurrent = () => scope.isCurrent() && snapshot.load === clock.current.load && focusRef.current === focus;
    commit(previous => {
      return { ...previous, loading: !previous.loaded, loadError: "" };
    });
    try {
      const [orderRows, candidateRows, management] = await Promise.all([
        purchaseOrderGateway.list(),
        isSpecialist ? purchaseOrderGateway.candidates() : Promise.resolve([]),
        isManager && !isRolePreview ? purchaseOrderGateway.managementOverview() : Promise.resolve(null),
      ]);
      if (!isCurrent()) return false;
      const writes = [...clock.current.orders.values()].filter(write => write.serial > snapshot.writes).map(write => write.order);
      const next = commit(previous => ({ scope,
        orders: mergePurchaseOrderRead(orderRows, previous.orders, writes),
        candidates: snapshot.candidates === clock.current.candidates ? candidateRows : previous.candidates,
        management, loading: false, loaded: true, loadError: "",
      }));
      if (!next) return false;
      const { orders, candidates } = next;
      for (const [id, write] of clock.current.orders) if (write.serial <= snapshot.writes) clock.current.orders.delete(id);
      if (initialRequestNo && handledFocus.current !== focus) {
        handledFocus.current = focus;
        const existingOrder = orders.find(order => order.requestNo === initialRequestNo);
        const candidate = candidates.find(item => item.requestNo === initialRequestNo);
        if (existingOrder) {
          const filter = purchaseOrderFocusFilter(existingOrder, isQuality);
          if (filter) callbacks.current.onFocusOrder(existingOrder, filter);
          else scope.notify("Bu sipariş henüz kalite kontrolü aşamasında değil; mevcut kalite listelerinde gösterilemiyor.", "error");
        }
        else if (candidate) callbacks.current.onFocusCandidate(candidate);
        else scope.notify("Bu nihai karara bağlı sipariş adayı veya sipariş kaydı bulunamadı.", "error");
      }
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      const message = error instanceof Error ? error.message : "Siparişler alınamadı.";
      commit(previous => ({ ...previous, loading: false, loadError: message }));
      scope.notify(message, "error");
      return false;
    }
  }, [scope, focus, initialRequestNo, isManager, isRolePreview, isSpecialist, isQuality, commit]);
  useEffect(() => { void load(); }, [load, dataRefreshVersion]);
  const visible = state.scope === scope ? state : empty(scope);
  return useMemo(() => ({ ...visible, scope, notify: scope.notify, dataLoaded: visible.loaded,
    isSpecialist, isPlanning, isQuality, isRolePreview, isManager, load, replaceOrder, setCandidates,
  }), [visible, scope, isSpecialist, isPlanning, isQuality, isRolePreview, isManager, load, replaceOrder, setCandidates]);
}
